import { useNavigate } from 'react-router-dom';
import {
  Factory,
  ShieldCheck,
  Truck,
  Wrench,
  BarChart3,
  Settings2,
  Search,
  Sparkles,
  Package,
  QrCode,
  Cpu,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { useAgentStore } from '@/agent/agentStore';
import { useChatPanel } from '@/stores/useChatPanel';

interface NavItem {
  label: string;
  path: string;
  keywords?: string;
}

const PRODUCTION: NavItem[] = [
  { label: 'Órdenes de trabajo', path: '/wip', keywords: 'wo work order ot' },
  { label: 'Despacho de lotes', path: '/dispatch', keywords: 'batch lote' },
  { label: 'Trazabilidad', path: '/traceability-search', keywords: 'trace sn' },
  { label: 'OEE / Analítica', path: '/analytics', keywords: 'oee' },
  { label: 'Paradas / Alertas', path: '/alerts', keywords: 'downtime paradas' },
];

const QUALITY: NavItem[] = [
  { label: 'Quality Board (NCs)', path: '/quality-board', keywords: 'nc ncs no conformidad' },
  { label: 'Inspecciones / SPC', path: '/quality-analytics', keywords: 'spc inspeccion' },
  { label: 'APQP / FAI', path: '/fai/dossier', keywords: 'apqp fai dossier' },
  { label: 'Detección de defectos', path: '/defect-detection', keywords: 'defect' },
];

const LOGISTICS: NavItem[] = [
  { label: 'SSCC / Etiquetado', path: '/barcode-analysis', keywords: 'sscc etiqueta gs1' },
  { label: 'Expediciones', path: '/warehouse-map', keywords: 'shipping expedicion' },
  { label: 'Almacén / Inventario', path: '/inventory-management', keywords: 'stock inventory' },
  { label: 'Búsqueda trazabilidad', path: '/traceability-search', keywords: 'cross-docking' },
];

const MAINTENANCE: NavItem[] = [
  { label: 'Mantenimiento predictivo', path: '/predictive-maintenance', keywords: 'gmao predictivo' },
  { label: 'Condition Monitoring', path: '/condition-monitoring', keywords: 'rutas vibracion' },
  { label: 'Cobotics', path: '/cobotics', keywords: 'robots' },
];

const REPORTS: NavItem[] = [
  { label: 'Dashboard ejecutivo', path: '/dashboard', keywords: 'kpi resumen' },
  { label: 'Reportes (OEE / calidad / energía)', path: '/reports', keywords: 'reportes informes' },
  { label: 'Quality analytics', path: '/quality-analytics' },
];

const BACKOFFICE: NavItem[] = [
  { label: 'Gestor de dispositivos', path: '/device-manager', keywords: 'devices camaras plc' },
  { label: 'Reglas / Rules engine', path: '/rules-engine', keywords: 'rules' },
  { label: 'Sync ERP', path: '/erp-sync', keywords: 'odoo erp' },
  { label: 'Admin panel', path: '/admin', keywords: 'admin usuarios' },
  { label: 'Audit log', path: '/audit-log' },
];

// Mock search results — simulan resolución contra MES/WMS
const MOCK_RESULTS: NavItem[] = [
  { label: 'WO-2026-0042 · Panel Solar 400W (Acme)', path: '/wo/WO-2026-0042', keywords: 'wo work order' },
  { label: 'WO-2026-0046 · Módulo Elevación 1000kg (Otis)', path: '/wo/WO-2026-0046' },
  { label: 'SN-78234 · Serial Number', path: '/traceability-search?q=SN-78234', keywords: 'serial' },
  { label: 'SSCC 00370123450000000017', path: '/traceability-search?q=SSCC0037', keywords: 'sscc' },
  { label: 'Máquina CNC-A2', path: '/condition-monitoring?asset=CNC-A2', keywords: 'machine maquina' },
];

const AGENT_PROMPTS: string[] = [
  'Muéstrame las NCs críticas',
  '¿Qué es lo más urgente?',
  'Traza SN-78234',
];

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const sendUserMessage = useAgentStore((s) => s.sendUserMessage);
  const openChat = useChatPanel((s) => s.setOpen);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const askAgent = (prompt: string) => {
    setOpen(false);
    openChat(true);
    sendUserMessage(prompt);
  };

  const renderGroup = (
    heading: string,
    Icon: React.ComponentType<{ className?: string }>,
    items: NavItem[],
  ) => (
    <CommandGroup heading={heading}>
      {items.map((item) => (
        <CommandItem
          key={`${heading}-${item.path}-${item.label}`}
          value={`${item.label} ${item.keywords ?? ''}`}
          onSelect={() => go(item.path)}
        >
          <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
          <span>{item.label}</span>
        </CommandItem>
      ))}
    </CommandGroup>
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Buscar páginas, WO, SN, SSCC… (Ctrl+K)" />
      <CommandList>
        <CommandEmpty>Sin resultados.</CommandEmpty>

        <CommandGroup heading="Resultados">
          {MOCK_RESULTS.map((r) => (
            <CommandItem
              key={r.path}
              value={`${r.label} ${r.keywords ?? ''}`}
              onSelect={() => go(r.path)}
            >
              {r.label.startsWith('WO') ? (
                <Package className="mr-2 h-4 w-4 text-muted-foreground" />
              ) : r.label.startsWith('SSCC') ? (
                <QrCode className="mr-2 h-4 w-4 text-muted-foreground" />
              ) : r.label.startsWith('Máquina') ? (
                <Cpu className="mr-2 h-4 w-4 text-muted-foreground" />
              ) : (
                <Search className="mr-2 h-4 w-4 text-muted-foreground" />
              )}
              <span>{r.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Agente EXPAI">
          {AGENT_PROMPTS.map((p) => (
            <CommandItem key={p} value={`agente ${p}`} onSelect={() => askAgent(p)}>
              <Sparkles className="mr-2 h-4 w-4 text-primary" />
              <span>{p}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />
        {renderGroup('Producción', Factory, PRODUCTION)}
        {renderGroup('Calidad', ShieldCheck, QUALITY)}
        {renderGroup('Logística', Truck, LOGISTICS)}
        {renderGroup('Mantenimiento', Wrench, MAINTENANCE)}
        {renderGroup('Reportes', BarChart3, REPORTS)}
        {renderGroup('Back Office', Settings2, BACKOFFICE)}
      </CommandList>
    </CommandDialog>
  );
}
